import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import tesoreriaService from '../../services/tesoreriaService';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BlockIcon from '@mui/icons-material/Block';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import PDFViewer from '../shared/PDFViewer';
import ExportProgressDialog, { ExportProgressState } from '../shared/ExportProgressDialog';
import AplicarSaldoReciboDialog from './AplicarSaldoReciboDialog';
import { parseNroTipoComprobante } from '../../constants/tiposComprobante';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import {
  fetchCompaniaParaPdf,
  fetchLogoPngDataUrl,
  lineasDatosCompania,
} from '../../utils/pdfLibroIvaCompania';
import { VerAsientoContableMenuItem } from '../shared/VerAsientoContableButton';
import SearchableSelect from '../SearchableSelect';
import FiltroSucursalVentaSelect from '../shared/FiltroSucursalVentaSelect';
import {
  resolveNroSucFiltro,
  useSucursalesVentaFiltro,
} from '../../hooks/useSucursalesVentaFiltro';
import {
  loadTesoreriaFiltros,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import { formatClienteConNro } from '../../utils/formatters';
import { hasBrowserSession } from '../../utils/sessionAuthStore';
import { useGridColumns, withColumnSx, type GridColumnDef } from '../../hooks/useGridColumns';
import { descubrirColumnasDeItems, presentarCampoGrilla } from '../../utils/gridFieldValue';
import {
  AccionesColumnHeader,
  PersonalizarColumnasDialog,
} from '../shared/PersonalizarColumnasDialog';
import type { SxProps, Theme } from '@mui/material/styles';
import { useRegisterPageTour } from '../tour/PageTourContext';
import { RECIBOS_LISTADO_TOUR } from '../../config/tours/recibosCobranzaTour';

type ReciboColumnId =
  | 'fecha'
  | 'comprobante'
  | 'cliente'
  | 'cuit'
  | 'importe'
  | 'formaDePago'
  | 'usuario'
  | 'estado'
  | 'acciones';

const RECIBOS_COLUMNAS: GridColumnDef<ReciboColumnId>[] = [
  { id: 'fecha', label: 'Fecha' },
  {
    id: 'comprobante',
    label: 'Comprobante',
    sx: { display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' },
  },
  { id: 'cliente', label: 'Cliente' },
  { id: 'cuit', label: 'CUIT' },
  { id: 'importe', label: 'Importe', align: 'right' },
  { id: 'formaDePago', label: 'Forma de Pago', defaultVisible: false },
  { id: 'usuario', label: 'Usuario' },
  { id: 'estado', label: 'Estado' },
  { id: 'acciones', label: 'Acciones', align: 'center', alwaysVisible: true },
];

const ESTADO_RECIBO_OPTIONS = [
  { value: 'activos', label: 'Activos' },
  { value: 'anulados', label: 'Anulados' },
  { value: 'todos', label: 'Todos' },
] as const;

type FiltroEstadoRecibo = (typeof ESTADO_RECIBO_OPTIONS)[number]['value'];

interface RecibosFiltrosStored {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  filtroEstado?: FiltroEstadoRecibo;
  filtroNroSuc?: number | null;
  rowsPerPage?: number;
}

const loadRecibosFiltros = (): RecibosFiltrosStored => {
  const parsed = loadTesoreriaFiltros<RecibosFiltrosStored>(TESORERIA_FILTROS_KEYS.recibos);
  const estadosValidos = ESTADO_RECIBO_OPTIONS.map((o) => o.value);
  if (parsed.filtroEstado && !estadosValidos.includes(parsed.filtroEstado)) {
    parsed.filtroEstado = 'todos';
  }
  return parsed;
};

interface Recibo {
  nroFormulario: number;
  fechaEmision: string;
  nroCliente: number;
  nombre: string;
  direccionCliente?: string | null;
  direccionEnvio?: string | null;
  importeTotal: number;
  formaDePago: string;
  usuario: string;
  cae_Nro: string;
  cae_Vencimiento: string;
  tipoComprobante: string;
  nroSuc: number;
  descripcion: string;
  cuit: string;
  letra?: string;
  nroComprobante?: number;
  asentado?: string;
  caeNro?: string;
  nroAsiento?: number | null;
}

interface RecibosResponse {
  items: Recibo[];
  totalCount: number;
}

const coerceNroAsiento = (raw: unknown): number | null => {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const mapRecibo = (item: any): Recibo => ({
  nroFormulario: (item.nroFormulario ?? item.NroFormulario ?? 0) as number,
  fechaEmision: (item.fechaEmision ?? item.FechaEmision ?? '') as string,
  nroCliente: (item.nroCliente ?? item.NroCliente ?? 0) as number,
  nombre: (item.nombre ?? item.Nombre ?? '') as string,
  direccionCliente: (item.direccionCliente ?? item.DireccionCliente ?? null) as string | null,
  direccionEnvio: (item.direccionEnvio ?? item.DireccionEnvio ?? null) as string | null,
  importeTotal: (item.importeTotal ?? item.ImporteTotal ?? 0) as number,
  formaDePago: (item.formaDePago ?? item.FormaDePago ?? '') as string,
  usuario: (item.usuario ?? item.Usuario ?? '') as string,
  cae_Nro: (item.cae_Nro ?? item.caE_Nro ?? item.CAE_Nro ?? '') as string,
  cae_Vencimiento: (item.cae_Vencimiento ?? item.caE_Vencimiento ?? item.CAE_Vencimiento ?? '') as string,
  tipoComprobante: (item.tipoComprobante ?? item.nroTipoFormulario ?? item.NroTipoFormulario ?? '') as string,
  nroSuc: (item.nroSuc ?? item.NroSuc ?? 0) as number,
  descripcion: (item.descripcion ?? item.Descripcion ?? '') as string,
  cuit: (item.cuit ?? item.CUIT ?? '') as string,
  letra: (item.letra ?? item.Letra) as string | undefined,
  nroComprobante: (item.nroComprobante ?? item.NroComprobante) as number | undefined,
  asentado: (item.asentado ?? item.Asentado) as string | undefined,
  caeNro: (item.caeNro ?? item.CaeNro) as string | undefined,
  nroAsiento: coerceNroAsiento(item.nroAsiento ?? item.NroAsiento),
});

const buildRecibosParams = (
  pageNum: number,
  size: number,
  searchValue: string | null,
  desde: string,
  hasta: string,
  estado: FiltroEstadoRecibo,
  nroSuc: number | null
) => {
  const params: Record<string, unknown> = {
    pageNumber: pageNum,
    pageSize: size,
    searchTerm: searchValue,
  };
  if (desde) params.fechaDesde = desde;
  if (hasta) params.fechaHasta = hasta;
  if (estado === 'anulados') params.asentado = 'A';
  else if (estado === 'activos') params.asentado = 'activos';
  const nroSucValue = resolveNroSucFiltro(nroSuc);
  if (nroSucValue != null) params.nroSuc = nroSucValue;
  return params;
};

const formatFechaCorta = (value: string) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('es-AR');
};

const Recibos: React.FC = () => {
  useRegisterPageTour(RECIBOS_LISTADO_TOUR);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const {
    columnasVisibles,
    columnasAnchorEl,
    setColumnasAnchorEl,
    gridColumns,
    toggleColumnaVisible,
    moverColumna,
    reordenarColumnas,
    agregarColumna,
    restablecerColumnas,
    columnasDefinidas,
  } = useGridColumns({
    gridKey: 'recibos',
    columns: RECIBOS_COLUMNAS,
  });

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [recibos, setRecibos] = useState<Recibo[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => loadRecibosFiltros().rowsPerPage ?? 10);
  const [searchTerm, setSearchTerm] = useState(() => loadRecibosFiltros().searchTerm ?? '');
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const guardado = loadRecibosFiltros().fechaDesde;
    if (guardado) return guardado;
    const fecha = getFirstDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const guardado = loadRecibosFiltros().fechaHasta;
    if (guardado) return guardado;
    const fecha = getLastDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedRecibo, setSelectedRecibo] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [listadoPdfUrl, setListadoPdfUrl] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgressState>({
    message: '',
    currentPage: 0,
    totalPages: 0,
  });
  const resetExportProgress = () =>
    setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
  const [aplicarDialogOpen, setAplicarDialogOpen] = useState(false);
  const [aplicarDialogRecibo, setAplicarDialogRecibo] = useState<Recibo | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstadoRecibo>(
    () => loadRecibosFiltros().filtroEstado ?? 'todos'
  );
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; reciboId: number } | null>(null);
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro({
    resetPreferidaOnNavigation: true,
  });

  const fetchRecibos = async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string,
    estado: FiltroEstadoRecibo,
    nroSuc: number | null
  ) => {
    try {
      if (!hasBrowserSession()) {
        enqueueSnackbar('No hay token de autenticación. Por favor, inicie sesión nuevamente.', { 
          variant: 'error',
          autoHideDuration: 3000
        });
        return;
      }

      const trimmed = search.trim();
      const response = await api.get<RecibosResponse>('/api/tesoreria/recibos', {
        params: buildRecibosParams(pageNumber + 1, pageSize, trimmed || null, desde, hasta, estado, nroSuc),
      });

      const rawItems = (response.data.items ?? (response.data as any).Items ?? []) as any[];
      setRecibos(Array.isArray(rawItems) ? rawItems.map(mapRecibo) : []);
      setTotalRecords(response.data.totalCount ?? (response.data as any).TotalCount ?? 0);
    } catch (error: any) {
      console.error('Error detallado:', error);
      const errorMessage = error.response?.data || 'Error al cargar los recibos';
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 3000
      });
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.recibos,
    { searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage },
    [searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!filtroListo) return;
    fetchRecibos(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc, filtroListo]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevoRecibo = () => {
    navigate('/tesoreria/recibos/nuevo');
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      if (!hasBrowserSession()) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }

      if (listadoPdfUrl) {
        window.URL.revokeObjectURL(listadoPdfUrl);
        setListadoPdfUrl(null);
      }

      const response = await api.get(
        `/api/tesoreria/recibos/${nroFormulario}/pdf`,
        {
          responseType: 'blob'
        }
      );
      
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedRecibo(nroFormulario);
    } catch (error) {
      enqueueSnackbar('Error al generar el PDF del recibo', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedRecibo(null);
    setPdfFileName(null);
  };

  const handleCloseListadoPDF = () => {
    if (listadoPdfUrl) {
      window.URL.revokeObjectURL(listadoPdfUrl);
    }
    setListadoPdfUrl(null);
  };

  const esReciboAnulado = (recibo: Recibo) =>
    (recibo.asentado ?? '').trim().toUpperCase() === 'A' || (recibo.nombre ?? '').trim().toUpperCase() === 'ANULADO';

  const getEstadoChip = (recibo: Recibo) => {
    if (esReciboAnulado(recibo)) {
      return {
        label: 'Anulado',
        bgColor: '#9e9e9e',
        textColor: '#ffffff',
      };
    }
    const caeNro = recibo.caeNro ?? recibo.cae_Nro;
    if (caeNro) {
      return {
        label: 'Con CAE',
        bgColor: '#4caf50',
        textColor: '#ffffff',
      };
    }
    return {
      label: 'Activo',
      bgColor: '#ff9800',
      textColor: '#ffffff',
    };
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatComprobante = (recibo: Recibo) => {
    const { letra, nroSuc, nroComprobante } = recibo;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  const fetchAllRecibosForExport = async (
    onProgress?: (currentPage: number, totalPages: number) => void
  ): Promise<{
    items: Recibo[];
    apiTotalCount: number;
    rawFetchedCount: number;
  }> => {
    // El backend de recibos pagina de a 20 (mismo límite que otros listados de tesorería).
    const fetchSize = 20;
    const maxPages = 500;
    const trimmed = searchTerm.trim();
    const all: Recibo[] = [];
    let apiPage = 1;
    let totalApi = 0;

    do {
      const response = await api.get<RecibosResponse>('/api/tesoreria/recibos', {
        params: buildRecibosParams(
          apiPage,
          fetchSize,
          trimmed || null,
          fechaDesde,
          fechaHasta,
          filtroEstado,
          filtroNroSuc
        ),
      });
      const rawItems = (response.data.items ?? (response.data as any).Items ?? []) as any[];
      const mapped = Array.isArray(rawItems) ? rawItems.map(mapRecibo) : [];
      all.push(...mapped);
      totalApi = response.data.totalCount ?? (response.data as any).TotalCount ?? 0;

      const totalPages = totalApi > 0 ? Math.max(1, Math.ceil(totalApi / fetchSize)) : 0;

      if (!rawItems?.length) {
        onProgress?.(Math.max(apiPage - 1, 1), apiPage > 1 ? apiPage - 1 : 1);
        break;
      }

      const fetchedAll = totalApi > 0 && all.length >= totalApi;
      const isLastPage = rawItems.length < fetchSize;

      if (fetchedAll || isLastPage || apiPage >= maxPages) {
        onProgress?.(apiPage, totalPages || apiPage);
        break;
      }

      onProgress?.(apiPage, totalPages);
      apiPage += 1;
    } while (all.length < totalApi && apiPage <= maxPages);

    return { items: all, apiTotalCount: totalApi, rawFetchedCount: all.length };
  };

  const getSucursalFiltroLabel = () => {
    if (filtroNroSuc == null || filtroNroSuc <= 0) return 'Todas';
    const suc = sucursales.find((s) => s.nroSuc === filtroNroSuc);
    if (!suc) return String(filtroNroSuc).padStart(5, '0');
    return suc.nombreSucursal?.trim()
      ? `${String(suc.nroSuc).padStart(5, '0')} — ${suc.nombreSucursal.trim()}`
      : String(suc.nroSuc).padStart(5, '0');
  };

  const getSucursalNombre = (nroSuc: number) => {
    const suc = sucursales.find((s) => s.nroSuc === nroSuc);
    const nombre = suc?.nombreSucursal?.trim();
    if (nombre) return nombre;
    return nroSuc ? String(nroSuc).padStart(5, '0') : '-';
  };

  const getEstadoFiltroLabel = () =>
    ESTADO_RECIBO_OPTIONS.find((o) => o.value === filtroEstado)?.label ?? filtroEstado;

  const handleExportPdf = async () => {
    if (totalRecords === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });

      const { items, apiTotalCount, rawFetchedCount } = await fetchAllRecibosForExport(
        (current, total) => {
          setExportProgress({
            message:
              total > 0
                ? `Obteniendo datos (${current} de ${total} páginas)...`
                : 'Obteniendo datos...',
            currentPage: current,
            totalPages: total,
          });
        }
      );

      if (items.length === 0) {
        enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
        return;
      }
      if (apiTotalCount > 0 && rawFetchedCount < apiTotalCount) {
        enqueueSnackbar(
          `Se exportaron ${items.length} de ${apiTotalCount} registros del servidor. Revise los filtros o intente nuevamente.`,
          { variant: 'warning' }
        );
      }

      setExportProgress({ message: 'Generando archivo PDF...', currentPage: 0, totalPages: 0 });

      const [compania, { jsPDF }, autoTableMod] = await Promise.all([
        fetchCompaniaParaPdf(),
        import('jspdf'),
        import('jspdf-autotable'),
      ]);
      const autoTable = autoTableMod.default;
      const logoPng = compania?.idCompania
        ? await fetchLogoPngDataUrl(compania.idCompania)
        : null;

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const pageW = doc.internal.pageSize.getWidth();
      const headerTop = 32;
      const logoMaxW = 76;
      const logoMaxH = 42;
      const rightColW = 228;
      const rightX = pageW - marginX;
      const totalImporte = items.reduce((sum, r) => sum + (Number(r.importeTotal) || 0), 0);

      if (logoPng) {
        doc.addImage(logoPng, 'PNG', marginX, headerTop, logoMaxW, logoMaxH, undefined, 'FAST');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(25, 55, 95);
      doc.text('Listado de recibos', pageW / 2, headerTop + 26, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(7);
      const datosComp = compania ? lineasDatosCompania(compania) : [];
      let ry = headerTop + 4;
      datosComp.forEach((linea) => {
        const wrapped = doc.splitTextToSize(linea, rightColW);
        wrapped.forEach((sub: string) => {
          doc.text(sub, rightX, ry, { align: 'right' });
          ry += 8;
        });
      });

      const headerBottom = Math.max(headerTop + logoMaxH + 4, ry + 4, headerTop + 48);
      let currentY = headerBottom;

      doc.setFontSize(8);
      const generadoEn = new Date();
      doc.text(`Generado: ${generadoEn.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 12;

      doc.text(
        `Período: ${formatFechaCorta(fechaDesde)} - ${formatFechaCorta(fechaHasta)}`,
        marginX,
        currentY
      );
      currentY += 12;

      const filtrosLinea = [
        `Sucursal: ${getSucursalFiltroLabel()}`,
        `Estado: ${getEstadoFiltroLabel()}`,
      ];
      if (searchTerm.trim()) {
        filtrosLinea.push(`Búsqueda: ${searchTerm.trim()}`);
      }
      doc.text(filtrosLinea.join('  ·  '), marginX, currentY);
      currentY += 12;

      doc.setFont('helvetica', 'bold');
      doc.text(
        `Registros: ${items.length}  ·  Total cobrado: ${formatCurrency(totalImporte)}`,
        marginX,
        currentY
      );
      doc.setFont('helvetica', 'normal');
      currentY += 14;

      const tableData = items.map((r) => [
        String(r.nroFormulario || ''),
        formatFechaCorta(r.fechaEmision),
        formatComprobante(r),
        (r.nombre || '').trim(),
        getSucursalNombre(r.nroSuc),
        (r.formaDePago || '').trim() || '-',
        formatCurrency(r.importeTotal || 0),
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [[
          'Nro. Recibo',
          'Fecha',
          'Comprobante',
          'Nombre',
          'Sucursal',
          'Forma de pago',
          'Importe',
        ]],
        body: tableData,
        foot: [[
          { content: 'Total', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: formatCurrency(totalImporte), styles: { halign: 'right', fontStyle: 'bold' } },
        ]],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        tableWidth: pageW - marginX * 2,
        styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'ellipsize', valign: 'middle' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8.5 },
        footStyles: {
          fillColor: [240, 244, 248],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 8.5,
        },
        columnStyles: (() => {
          const tableWidth = pageW - marginX * 2;
          // Anchos más equivalentes entre columnas
          const ratios = [0.13, 0.13, 0.16, 0.18, 0.12, 0.14, 0.14];
          return {
            0: { cellWidth: tableWidth * ratios[0] },
            1: { cellWidth: tableWidth * ratios[1] },
            2: { cellWidth: tableWidth * ratios[2] },
            3: { cellWidth: tableWidth * ratios[3], overflow: 'ellipsize' },
            4: { cellWidth: tableWidth * ratios[4] },
            5: { cellWidth: tableWidth * ratios[5], overflow: 'ellipsize' },
            6: { cellWidth: tableWidth * ratios[6], halign: 'right' },
          };
        })(),
      });

      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setSelectedRecibo(null);
        setPdfFileName(null);
      }

      const pdfBlob = doc.output('blob');
      const safeBlob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setListadoPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al generar PDF:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al generar el PDF', {
        variant: 'error',
      });
    } finally {
      setExportingPdf(false);
      resetExportProgress();
    }
  };

  const handleAbrirAplicarSaldo = (recibo: Recibo) => {
    setAplicarDialogRecibo(recibo);
    setAplicarDialogOpen(true);
  };

  const handleCloseAplicarDialog = () => {
    setAplicarDialogOpen(false);
    setAplicarDialogRecibo(null);
  };

  const puedeMostrarAnular = (recibo: Recibo) => {
    if (esReciboAnulado(recibo)) return false;
    const cae = (recibo.caeNro ?? recibo.cae_Nro ?? '').trim();
    return !cae;
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, reciboId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, reciboId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const currentKebabRecibo = kebabAnchor
    ? recibos.find(r => r.nroFormulario === kebabAnchor.reciboId)
    : null;

  const renderKebabItems = (recibo: Recibo) => {
    const menuItems: React.ReactElement[] = [
      <VerAsientoContableMenuItem
        key="asiento"
        nroAsiento={recibo.nroAsiento}
        onClose={handleKebabClose}
      />,
    ];
    if (puedeMostrarAnular(recibo)) {
      menuItems.push(
        <MenuItem
          key="anular"
          onClick={() => {
            handleKebabClose();
            void handleAnular(recibo);
          }}
          disabled={anulandoId === recibo.nroFormulario}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <BlockIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Anular recibo</ListItemText>
        </MenuItem>
      );
    }
    return menuItems;
  };

  const handleAnular = async (recibo: Recibo) => {
    if (!puedeMostrarAnular(recibo)) return;

    try {
      const check = await tesoreriaService.puedeAnularRecibo(recibo.nroFormulario);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular este recibo', { variant: 'warning' });
        return;
      }
    } catch (error: any) {
      const msg = error?.response?.data?.motivos?.[0] ?? error?.response?.data?.message ?? 'No se pudo validar la anulación';
      enqueueSnackbar(msg, { variant: 'error' });
      return;
    }

    if (
      !window.confirm(
        '¿Está seguro que desea anular este recibo? Se revertirán las imputaciones a facturas y los medios de cobro asociados.'
      )
    ) {
      return;
    }

    setAnulandoId(recibo.nroFormulario);
    try {
      await tesoreriaService.anularRecibo(recibo.nroFormulario);
      enqueueSnackbar('Recibo anulado correctamente', { variant: 'success' });
      fetchRecibos(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc);
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        data?.message ??
        (Array.isArray(data?.errors?.general) ? data.errors.general[0] : null) ??
        'Error al anular el recibo';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setAnulandoId(null);
    }
  };

  const columnasParaAgregar = useMemo(
    () => descubrirColumnasDeItems(recibos, gridColumns.columnOrder),
    [gridColumns.columnOrder, recibos],
  );

  const renderCeldaRecibo = (columnId: string, recibo: Recibo, colSx: SxProps<Theme> | undefined) => {
    switch (columnId) {
      case 'fecha':
        return <TableCell key={columnId} sx={colSx}>{new Date(recibo.fechaEmision).toLocaleDateString()}</TableCell>;
      case 'comprobante':
        return <TableCell key={columnId} sx={colSx}>{formatComprobante(recibo)}</TableCell>;
      case 'cliente':
        return (
          <TableCell key={columnId} sx={withColumnSx({ fontWeight: 600 }, colSx)}>
            {formatClienteConNro(recibo.nroCliente, recibo.nombre)}
          </TableCell>
        );
      case 'cuit':
        return <TableCell key={columnId} sx={colSx}>{recibo.cuit || '-'}</TableCell>;
      case 'importe':
        return (
          <TableCell key={columnId} align="right" sx={withColumnSx({ fontWeight: 600, color: '#1976d2' }, colSx)}>
            {formatCurrency(recibo.importeTotal)}
          </TableCell>
        );
      case 'formaDePago':
        return <TableCell key={columnId} sx={colSx}>{recibo.formaDePago || '-'}</TableCell>;
      case 'usuario':
        return <TableCell key={columnId} sx={colSx}>{recibo.usuario || '-'}</TableCell>;
      case 'estado': {
        const estadoChip = getEstadoChip(recibo);
        return (
          <TableCell key={columnId} sx={colSx}>
            <Chip
              label={estadoChip.label}
              size="small"
              sx={{
                bgcolor: estadoChip.bgColor,
                color: estadoChip.textColor,
                fontWeight: 500,
                borderRadius: 1.5,
                minWidth: 90,
              }}
            />
          </TableCell>
        );
      }
      case 'acciones':
        return (
          <TableCell key={columnId} align="center" sx={colSx}>
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="center"
              {...(recibo.nroFormulario === recibos[0]?.nroFormulario
                ? { 'data-tour-id': 'recibos-acciones' }
                : {})}
            >
              <Tooltip title="Imprimir PDF">
                <IconButton
                  onClick={() => handleImprimirPDF(recibo.nroFormulario)}
                  size="small"
                  sx={{
                    bgcolor: '#2196f3',
                    color: '#fff',
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                    transition: 'all 0.2s'
                  }}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!esReciboAnulado(recibo) && (
                <Tooltip title="Aplicar saldo / Ver aplicaciones">
                  <IconButton
                    onClick={() => handleAbrirAplicarSaldo(recibo)}
                    size="small"
                    sx={{
                      bgcolor: '#2e7d32',
                      color: '#fff',
                      borderRadius: 1.5,
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
                      transition: 'all 0.2s'
                    }}
                  >
                    <AccountBalanceWalletIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Más acciones">
                <IconButton
                  size="small"
                  onClick={(e) => handleKebabOpen(e, recibo.nroFormulario)}
                  sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        );
      default: {
        const campo = presentarCampoGrilla(recibo, columnId);
        return (
          <TableCell key={columnId} align={campo.align} sx={colSx}>
            {campo.texto}
          </TableCell>
        );
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Recibos
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handleExportPdf()}
                data-tour-id="recibos-exportar"
                disabled={exportingPdf || totalRecords === 0}
                sx={{ borderRadius: 2, px: 2, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                {exportingPdf ? 'Generando PDF…' : 'Imprimir PDF'}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate('/tesoreria/devoluciones')}
                sx={{ borderRadius: 2, px: 2, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Devoluciones
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleNuevoRecibo}
                data-tour-id="recibos-nuevo"
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                Nuevo Recibo
              </Button>
            </Stack>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }} data-tour-id="recibos-filtros">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por nro. recibo, nro. comprobante, descripción, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Estado"
                  options={[...ESTADO_RECIBO_OPTIONS]}
                  value={filtroEstado}
                  onChange={(value) => {
                    if (value) {
                      setFiltroEstado(value as FiltroEstadoRecibo);
                      setPage(0);
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2} data-tour-id="recibos-grilla">
              {recibos.map((recibo) => {
                const estadoChip = getEstadoChip(recibo);
                const comprobante = formatComprobante(recibo);
                return (
                  <Card 
                    key={recibo.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {comprobante !== '-' ? comprobante : 'Recibo'}
                            </Typography>
                          </Box>
                          <Chip
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {new Date(recibo.fechaEmision).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {formatClienteConNro(recibo.nroCliente, recibo.nombre)}
                        </Typography>
                        {recibo.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {recibo.cuit}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe:</strong> {formatCurrency(recibo.importeTotal)}
                        </Typography>
                        {recibo.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {recibo.usuario}
                          </Typography>
                        )}
                        {recibo.cae_Nro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {recibo.cae_Nro}
                          </Typography>
                        )}
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 1 }}
                          {...(recibo.nroFormulario === recibos[0]?.nroFormulario
                            ? { 'data-tour-id': 'recibos-acciones' }
                            : {})}
                        >
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(recibo.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                            {!esReciboAnulado(recibo) && (
                              <Tooltip title="Aplicar saldo / Ver aplicaciones">
                                <IconButton
                                  onClick={() => handleAbrirAplicarSaldo(recibo)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2e7d32',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#1b5e20' }
                                  }}
                                >
                                  <AccountBalanceWalletIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Más acciones">
                              <IconButton
                                size="small"
                                onClick={(e) => handleKebabOpen(e, recibo.nroFormulario)}
                                sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {recibos.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay recibos para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              data-tour-id="recibos-grilla"
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {columnasVisibles.map((col) => (
                      <TableCell key={col.id} align={col.align} sx={withColumnSx({ fontWeight: 600 }, col.sx)}>
                        {col.id === 'acciones' ? (
                          <AccionesColumnHeader label={col.label} onCustomize={(e) => setColumnasAnchorEl(e.currentTarget)} />
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recibos.map((recibo) => (
                    <TableRow
                      key={recibo.nroFormulario}
                      sx={{
                        '&:hover': {
                          bgcolor: '#f9f9f9',
                        },
                      }}
                    >
                      {columnasVisibles.map((col) => renderCeldaRecibo(col.id, recibo, col.sx))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedRecibo && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Recibo ${selectedRecibo}`}
          documentId={`${selectedRecibo}`}
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      {listadoPdfUrl && (
        <PDFViewer
          pdfUrl={listadoPdfUrl}
          onClose={handleCloseListadoPDF}
          title="Listado de recibos"
          documentId="listado-recibos"
          documentType="informeCliente"
          downloadFileName={`Listado_Recibos_${new Date().toISOString().split('T')[0]}.pdf`}
        />
      )}

      <ExportProgressDialog open={exportingPdf} progress={exportProgress} />

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabRecibo && renderKebabItems(currentKebabRecibo)}
      </Menu>

      {aplicarDialogRecibo && (
        <AplicarSaldoReciboDialog
          open={aplicarDialogOpen}
          onClose={handleCloseAplicarDialog}
          reciboId={aplicarDialogRecibo.nroFormulario}
          nroCliente={aplicarDialogRecibo.nroCliente}
          nombreCliente={aplicarDialogRecibo.nombre || `Cliente ${aplicarDialogRecibo.nroCliente}`}
          reciboAnulado={esReciboAnulado(aplicarDialogRecibo)}
          nroTipoFormularioCredito={
            parseNroTipoComprobante(aplicarDialogRecibo.tipoComprobante) || '4'
          }
          letraCredito={aplicarDialogRecibo.letra}
          nroSucCredito={aplicarDialogRecibo.nroSuc}
          nroComprobanteCreditoNum={aplicarDialogRecibo.nroComprobante}
          importeTotalCredito={aplicarDialogRecibo.importeTotal}
          onSuccess={() => fetchRecibos(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc)}
        />
      )}

      <PersonalizarColumnasDialog
        anchorEl={columnasAnchorEl}
        onClose={() => setColumnasAnchorEl(null)}
        onReorder={reordenarColumnas}
        columns={columnasDefinidas}
        columnOrder={gridColumns.columnOrder}
        visibleIds={gridColumns.visibleIds}
        onToggle={toggleColumnaVisible}
        onMove={moverColumna}
        onReset={restablecerColumnas}
        addableColumns={columnasParaAgregar}
        onAdd={agregarColumna}
      />
    </Box>
  );
};

export default Recibos; 